import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Book,
  BookOpen,
  Calendar,
  Check,
  CircleCheck,
  Clock,
  Download,
  SquarePen,
  Target,
  AlignLeft,
  ListChecks,
} from "lucide-react";
import Button from "../../components/Button/Button";
import Card from "../../components/Card/Card";
import Loader from "../../components/Loader/Loader";
import DataNotFound from "../../utils/DataNotFound";
import { enqueueSnackbar } from "notistack";
import { getLessonPlanById } from "../../services/LessonPlanService";
import { handleApiError } from "../../utils/errorHandler";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import StatusChip from "../../components/StatusChip/StatusChip";
import { LESSON_STATUS, SECTION_TYPES } from "./LessonPlannerConstant";
import { REVIEW_TAGS } from "../WeeklyDiary/WeeklyDiaryConstant";
import {
  formatDate,
  formatTimeRange,
  getPhaseMeta,
  getSubjectTone,
  getTotalDuration,
  normalizeLesson,
} from "./utils/lessonPlanUtils";
import { downloadLessonPlanPdf } from "./utils/lessonPlanPdf";
import "./LessonPlanner.css";

// Material names are free text; turn any URL inside them into a clickable link
const URL_PATTERN = /((?:https?:\/\/|www\.)[^\s]+)/gi;

const linkify = (text) =>
  String(text ?? "").split(URL_PATTERN).map((part, i) => {
    if (i % 2 === 0) return part;
    const href = /^https?:\/\//i.test(part) ? part : `https://${part}`;
    return (
      <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="lp-material-link">
        {part}
      </a>
    );
  });

const DetailHeading = ({ icon: Icon, title, meta }) => (
  <div className="lp-detail-heading">
    <span className="lp-form-section__icon">
      <Icon size={14} />
    </span>
    <h3>{title}</h3>
    {meta && <span className="text-muted small">· {meta}</span>}
  </div>
);

const LessonPlanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  const fetchLesson = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getLessonPlanById(id);
      setLesson(normalizeLesson(response.data.data));
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLesson();
  }, [fetchLesson]);

  const back = () => navigate("/lesson-planner");

  const exportPdf = async () => {
    setIsExporting(true);
    try {
      await downloadLessonPlanPdf(lesson);
    } catch (error) {
      console.error("Lesson plan PDF export failed:", error);
      enqueueSnackbar("Could not generate the PDF. Please try again.", { variant: "error" });
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading || !lesson) {
    return (
      <Card componentName="Lesson Details" back={back}>
        {isLoading ? <Loader /> : <DataNotFound />}
      </Card>
    );
  }

  const tone = getSubjectTone(lesson.subjectName);
  const isCompleted = lesson.status === LESSON_STATUS.COMPLETED;
  const totalDuration = getTotalDuration(lesson.phases);
  const time = formatTimeRange(lesson.startTime, lesson.endTime);

  const glance = [
    {
      label: "Subject",
      value: lesson.subjectName,
      className: `lp-text-${tone}`,
    },
    { label: "Class", value: lesson.programName },
    { label: "Duration", value: `${totalDuration} min` },
    { label: "Objectives", value: lesson.objectives.length },
    { label: "Status", value: isCompleted ? "Completed" : "Pending" },
  ];

  return (
    <Card
      componentName="Lesson Details"
      back={back}
      freezed={
        !isCompleted && (
          <Button
            variant="outline-primary"
            className="mx-1"
            icon={<SquarePen size={14} />}
            onClick={() => navigate(`/lesson-planner/update/${lesson.id}`)}
          >
            Edit
          </Button>
        )
      }
    >
      <div className="lp-detail lp-detail__layout">
        {/* ================= MAIN ================= */}
        <div className="lp-detail__main">
          <div className={`card lp-detail-hero lp-accent-${tone}`}>
            <div className="lp-lesson-card__meta">
              <StatusChip tone={isCompleted ? "success" : "warning"}>
                {isCompleted ? "Completed" : "Pending"}
              </StatusChip>
              {lesson.isFlagged && (
                <StatusChip tone="danger">Flagged</StatusChip>
              )}
              {/* Tag set by the principal while reviewing the weekly diary */}
              {REVIEW_TAGS[lesson.reviewTag] && (
                <StatusChip tone={REVIEW_TAGS[lesson.reviewTag].tone}>
                  {REVIEW_TAGS[lesson.reviewTag].label}
                </StatusChip>
              )}
              <span className={`lp-dot lp-bg-${tone}`} />
              <span>
                {lesson.subjectName} · {lesson.programName}
              </span>
            </div>
            <h2>{lesson.title}</h2>
            <div className="lp-detail-hero__facts">
              {lesson.lessonDate && (
                <span>
                  <Calendar size={14} />{" "}
                  {formatDate(lesson.lessonDate, "EEEE, d MMM")}
                </span>
              )}
              {time && (
                <span>
                  <Clock size={14} /> {time}
                </span>
              )}
              {lesson.unitChapter && (
                <span>
                  <BookOpen size={14} /> {lesson.unitChapter}
                </span>
              )}
            </div>
          </div>

          {lesson.remark && (
            <div className="lp-remark">
              <strong>Teacher Remarks</strong>
              <p>{lesson.remark}</p>
            </div>
          )}

          {lesson.isFlagged && (lesson.flagRemarks || lesson.revisedDeliveryDate) && (
            <div className="lp-remark">
              {lesson.flagRemarks && (
                <>
                  <strong>Flag Remarks</strong>
                  <p>{lesson.flagRemarks}</p>
                </>
              )}
              {lesson.revisedDeliveryDate && (
                <>
                  <strong>Revised Delivery Date</strong>
                  <p>{formatDate(lesson.revisedDeliveryDate, "EEEE, d MMM yyyy")}</p>
                </>
              )}
            </div>
          )}

          {lesson.objectives.length > 0 && (
            <>
              <DetailHeading icon={Target} title="Learning objectives" />
              <ul className="lp-objectives">
                {lesson.objectives.map((objective, index) => (
                  <li key={index}>
                    <span className="lp-objectives__check">
                      <Check size={12} />
                    </span>
                    {objective}
                  </li>
                ))}
              </ul>
            </>
          )}

          {lesson.phases.length > 0 && (
            <>
              <DetailHeading
                icon={AlignLeft}
                title="Lesson flow"
                meta={`${totalDuration} min total`}
              />
              <div className="lp-flow">
                {lesson.phases.map((phase) => {
                  const meta = getPhaseMeta(phase.phase);
                  return (
                    <div key={phase.phase} className="lp-flow__item">
                      <div className="lp-flow__header">
                        <strong className={`lp-text-${meta.tone}`}>
                          {meta.label}
                        </strong>
                        <span className="text-muted small">
                          {phase.duration_minutes} min
                        </span>
                      </div>
                      <p>{phase.content || "—"}</p>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {lesson.assessment && (
            <>
              <DetailHeading icon={CircleCheck} title="Assessment" />
              <p className="lp-detail-text">{lesson.assessment}</p>
            </>
          )}

          {lesson.homework && (
            <>
              <DetailHeading icon={Book} title="Homework" />
              <p className="lp-detail-text">{lesson.homework}</p>
            </>
          )}

          {lesson.sections.map((section, index) => (
            <div key={index}>
              <DetailHeading
                icon={
                  section.type === SECTION_TYPES.BULLET_LIST
                    ? ListChecks
                    : AlignLeft
                }
                title={section.name}
                meta={section.sub_heading}
              />
              {section.type === SECTION_TYPES.BULLET_LIST ? (
                <ul className="lp-detail-list">
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </ul>
              ) : (
                // Rich text is authored in CKEditor by teachers of this organisation
                <div
                  className="lp-detail-text lp-rich-content"
                  // eslint-disable-next-line react/no-danger -- sanitized via DOMPurify in sanitizeHtml() above
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.content) }}
                />
              )}
            </div>
          ))}
        </div>

        {/* ================= SIDEBAR ================= */}
        <aside className="lp-detail__aside">
          <div className="card lp-panel">
            <h3>At a glance</h3>
            <dl className="lp-glance">
              {glance.map(({ label, value, className }) => (
                <div key={label}>
                  <dt>{label}</dt>
                  <dd className={className}>{value || "—"}</dd>
                </div>
              ))}
            </dl>
          </div>

          {lesson.materials.length > 0 && (
            <div className="card lp-panel">
              <h3>Materials &amp; resources</h3>
              <ul className="lp-materials">
                {lesson.materials.map((material) => (
                  <li key={material}>
                    <span className="lp-stat-card__icon lp-tone-warning">
                      <Book size={14} />
                    </span>
                    <div>
                      <strong>{linkify(material)}</strong>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Button
            variant="outline-primary"
            block
            iconRight={<Download size={14} />}
            onClick={exportPdf}
            loading={isExporting}
          >
            Download PDF
          </Button>
        </aside>
      </div>
    </Card>
  );
};

export default LessonPlanDetail;
